import {
  Box,
  CircularProgress,
  Drawer,
  Grid,
  PaginationItem,
  Paper,
  Typography,
} from '@mui/material';
import MuiPagination from '@mui/material/Pagination';
import { styled } from '@mui/material/styles';
import { GridPagination } from '@mui/x-data-grid-pro';
import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';

import {
  deleteIssue,
  deleteIssueBulk,
  saveIssueEdit,
} from '../../../../packages/common/api';
import WarningModal from '../../../../packages/common/shared-ui/src/components/WarningModal';
import PaginationNextButtonIcon from '../../../../packages/common/shared-ui/src/icons/PaginationNextButtonIcon';
import PaginationPreviousButtonIcon from '../../../../packages/common/shared-ui/src/icons/PaginationPreviousButtonIcon';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';
import ContextMenu from '../../issues/ContextMenu/ContextMenu';
import AgileIssue from '../AgileIssue/AgileIssue';

// Original class: agileTableContainer
const StyledAgileTableContainer = styled(Grid)(theme => ({
  overflowX: 'auto',
  overflowY: 'hidden',
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#F1F2F8',
    marginRight: '2px',
  },

  '&::-webkit-scrollbar': {
    height: '6px',
    backgroundColor: 'white !important',
  },

  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#7174AC',
    borderRadius: '5px',
  },
}));
// Original class: column
const StyledColumn = styled(Paper)(theme => ({
  overflowY: 'auto',

  '&::-webkit-scrollbar-track': {
    backgroundColor: '#E4E4EF',
  },

  '&::-webkit-scrollbar': {
    width: '4px',
  },

  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#7174AC',
    borderRadius: '5px',
  },
}));
// Original class: tag
const StyledTag = styled(Box)(theme => ({
  padding: '0 8px',
  border: '1px solid #E4E4EF',
  borderRadius: '8px',
  marginRight: '4px',
  marginTop: '4px',
}));
// Original class: paginationContainer
const StyledPaginationContainer = styled(Grid)(theme => ({
  '& .MuiPagination-root': {
    marginLeft: 0,
    '& li': {
      fontSize: '16px',
      lineHeight: '20px',
    },
    '& li + li': {
      marginLeft: '2px',
    },
    '& .MuiButtonBase-root.MuiPaginationItem-root': {
      color: '#41424E',
      fontSize: '16px',
      lineHeight: '20px',
      borderRadius: '4px',
      minWidth: '28px',
      height: '28px',
      margin: 0,
      '&.Mui-selected': {
        backgroundColor: '#6270D0',
        color: '#FAFAFC',
        '&:hover': {
          color: '#FAFAFC',
          backgroundColor: '#6270D0',
        },
      },
      '& .MuiSvgIcon-root.MuiPaginationItem-icon': {
        width: 'auto',
        height: 'auto',
      },
      '&:hover': {
        color: '#6270D0',
        backgroundColor: 'transparent',
      },
    },
    '& .MuiPaginationItem-ellipsis': {
      fontSize: '16px',
      lineHeight: '20px',
      color: '#41424E',
      padding: 0,
      margin: 0,
      minWidth: ' 28px',
    },
  },
}));
// Original class: avatar
const StyledAvatar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 32,
  minHeight: 32,
  maxWidth: 32,
  maxHeight: 32,
  borderRadius: theme.spacing(4),
  overflow: 'hidden',
  backgroundColor: '#37C670',
}));

const AgileTable = ({
  agileData,
  agileRulesData,
  paginationData,
  changePagination,
  indicators,
}) => {
  const pageParams = useParams();
  const getLocalString = useTranslation();
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [menuIssue, setMenuIssue] = useState(null);
  const [agileRules, setAgileRules] = useState([]);
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const [showDeleteIssueWarning, setShowDeleteIssueWarning] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0 });
  const [pagination, setPagination] = useState({ count: 0, page: 0 });
  const [openWarningModal, setOpenWarningModal] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const user = useSelector(state => state.user.info);

  useEffect(() => {
    if (paginationData) {
      setPagination(() => ({
        page: paginationData.offset / 500,
        count: Math.ceil(paginationData.total / paginationData.limit),
      }));
    }
  }, [paginationData]);

  useEffect(() => {
    if (agileData && agileData.statuses && agileData.rows) {
      const formattedColumns = agileData.statuses.map(column => {
        let currentColumn = column;
        if (agileData.rows.length > 0) {
          const item =
            agileData.rows.length === 2 ? agileData.rows[1] : agileData.rows[2];
          const group = item.groups.find(
            item => item.statusId === column.statusId,
          );
          if (group !== undefined) {
            currentColumn.issues = group.issues;
          } else {
            currentColumn.issues = [];
          }
        } else {
          currentColumn.issues = [];
        }

        return currentColumn;
      });
      setColumns(formattedColumns);
    }
  }, [agileData]);

  useEffect(() => {
    agileRulesData && setAgileRules(agileRulesData);
  }, [agileRulesData]);

  const onDragStart = useCallback(
    start => {
      const statusId = start.source.droppableId;
      const issueId = start.draggableId;
      const sourceColumn =
        columns.find(item => item.statusId === Number(statusId)) ?? null;
      const draggableItem =
        sourceColumn.issues.find(item => item.issueId === issueId) ?? null;
      const availiableStatuses = agileRules.find(
        item =>
          item.statusId === Number(statusId) &&
          item.trackerId === Number(draggableItem.services.trackerId),
      );
      let availiableColumns = [];
      if (availiableStatuses !== undefined) {
        availiableStatuses.statuses.push(Number(start.source.droppableId));
        availiableColumns = columns.map(item => {
          if (!availiableStatuses.statuses.includes(item.statusId)) {
            return {
              ...item,
              isDisabled: true,
            };
          } else {
            return item;
          }
        });
      } else {
        availiableColumns = columns.map(item => {
          if (item.statusId !== Number(statusId)) {
            return {
              ...item,
              isDisabled: true,
            };
          } else {
            return item;
          }
        });
      }
      setColumns(availiableColumns);
    },
    [agileRules, columns],
  );

  const updateIssue = useCallback(async (id, data, type) => {
    const params = {};
    type === 'status' && (params.statusId = data);
    type === 'assigned' && (params.assignedTo = data);
    type === 'priority' && (params.priority = data);
    try {
      setLoading(true);
      return await saveIssueEdit(id, params);
    } catch (error) {
      console.error('ERROR WITH EDIT', error);
    }
  }, []);

  const moveIssue = useCallback(
    (source, destination, id, sourceIndex, destinationIndex = 0) => {
      const sourceColumn =
        columns.find(item => item.statusId === Number(source)) ?? null;
      const destinationColumn =
        columns.find(item => item.statusId === Number(destination)) ?? null;
      const draggableItem =
        sourceColumn.issues.find(item => item.issueId === id) ?? null;
      if (!sourceColumn && !destinationColumn && !draggableItem) {
        return;
      }
      return columns.map(item => {
        const column = item;
        let itemIssues = item.issues;
        if (item.statusId === sourceColumn.statusId) {
          itemIssues.splice(sourceIndex, 1);
          column.count = column.count - 1;
        }
        if (destinationColumn && item.statusId === destinationColumn.statusId) {
          draggableItem.services.statusId = destination.toString();
          itemIssues.splice(destinationIndex, 0, draggableItem);
          column.count = column.count + 1;
        }
        column.isDisabled && delete column.isDisabled;

        return column;
      });
    },
    [columns],
  );

  const onDragEnd = useCallback(
    result => {
      const updatedColumns = moveIssue(
        result.source.droppableId,
        result.destination.droppableId,
        result.draggableId,
        result.source.index,
        result.destination.index,
      );
      if (result.source.droppableId !== result.destination.droppableId) {
        updateIssue(
          result.draggableId,
          result.destination.droppableId,
          'status',
        ).then(res => {
          if (res) {
            setColumns(updatedColumns);
            setLoading(false);
          }
        });
      } else {
        setColumns(updatedColumns);
      }
    },
    [moveIssue, updateIssue],
  );

  const handleLinkClick = useCallback(event => {
    event.stopPropagation();
  }, []);

  const handleOpenContextMenu = useCallback((event, id) => {
    event.preventDefault();
    setMenuPosition({
      top: event.clientY,
      left: event.clientX,
    });
    setMenuIssue(id);
    setIsContextMenuOpen(true);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setIsContextMenuOpen(false);
    setMenuIssue(null);
    setMenuPosition({ left: 0, top: 0 });
  }, []);

  const handleSubMenuSelect = useCallback(
    (data, type) => {
      updateIssue(menuIssue.issueId, data.valueId, type).then(res => {
        if (res) {
          const column =
            columns.find(
              item => item.statusId.toString() === menuIssue.services.statusId,
            ) ?? null;
          const issue =
            column.issues.find(item => item.issueId === menuIssue.issueId) ??
            null;
          const pos = column.issues.map(e => e.issueId).indexOf(issue.issueId);
          if (!column && !issue) {
            return;
          }
          if (type === 'status') {
            const updatedColumns = moveIssue(
              menuIssue.services.statusId,
              data.valueId,
              issue.issueId,
              pos,
            );
            setColumns(updatedColumns);
          } else {
            const columnPos = columns
              .map(e => e.statusId)
              .indexOf(Number(menuIssue.services.statusId));
            let fieldName;
            type === 'priority' && (fieldName = 'issue.priority');
            type === 'assigned' && (fieldName = 'issue.assignedTo');
            let field =
              issue.fields.find(item => item.fieldId === fieldName) ?? null;
            if (field) {
              field = {
                ...field,
                value: data.valueId ? data.valueName : null,
                valueId: data.valueId ? data.valueId : null,
              };
              type === 'priority' && data.color && (field.color = data.color);
              issue.fields = issue.fields.map(item => {
                if (item.fieldId === fieldName) {
                  return field;
                } else {
                  return item;
                }
              });
              column.issues = [
                ...column.issues.slice(0, pos),
                issue,
                ...column.issues.slice(pos + 1),
              ];
              setColumns([
                ...columns.slice(0, columnPos),
                column,
                ...columns.slice(columnPos + 1),
              ]);
            }
          }
          setLoading(false);
          //handleCloseMenu();
        }
      });
    },
    [columns, menuIssue, moveIssue, updateIssue],
  );

  const handleCloseIssue = useCallback(() => {
    setMenuIssue(null);
    setDrawerOpen(false);
  }, []);

  const handleDeleteIssue = useCallback(async () => {
    setLoading(true);
    try {
      let params = {};
      const response = await deleteIssue(menuIssue.issueId, params);
      if (response) {
        let sourceColumn = columns.find(
          item => item.statusId === Number(menuIssue.services.statusId),
        );
        let updatedColumns = columns;
        sourceColumn.issues = sourceColumn.issues.filter(item => {
          return item.issueId !== menuIssue.issueId;
        });
        updatedColumns = updatedColumns.map(item => {
          if (item.statusId === Number(menuIssue.services.statusId)) {
            return {
              ...sourceColumn,
              count: sourceColumn.count - 1,
            };
          } else {
            return item;
          }
        });
        setColumns(updatedColumns);
        handleCloseMenu();
        handleCloseIssue();
        setOpenWarningModal(false);
        setLoading(false);
      }
    } catch (error) {
      error.response.status === 420 && setShowDeleteIssueWarning(true);
      console.error('ERROR WITH DELETE', error);
    }
  }, [columns, handleCloseMenu, menuIssue, handleCloseIssue]);

  const handleWarningModal = useCallback(() => {
    setOpenWarningModal(true);
  }, []);

  const handleMenuSelect = useCallback(
    type => {
      if (type === 'edit') {
        let url = '';
        url = `/issues/${menuIssue.issueId}/edit`;
        if (pageParams.projectId) {
          url = `/projects/${pageParams.projectId}/issues/${menuIssue.issueId}/edit`;
        }
        handleCloseMenu();
        window.open(url, '_blank');
      }
      if (type === 'copy') {
        let url = `/issues/${menuIssue.issueId}/copy?trackerId=${menuIssue.services.trackerId}&projectId=${menuIssue.services.projectId}`;
        if (pageParams.projectId) {
          url = `/projects/${pageParams.projectId}/issues/${menuIssue.issueId}/copy?trackerId=${menuIssue.services.trackerId}&projectId=${menuIssue.services.projectId}&list`;
        }
        handleCloseMenu();
        window.open(url, '_blank');
      }
      if (type === 'delete') {
        handleWarningModal();
      }
    },
    [handleCloseMenu, handleWarningModal, menuIssue, pageParams.projectId],
  );

  const getName = useCallback(
    data => {
      let userName = data.value;
      user.redmineId === Number(data.valueId) && (userName = user.fullName);
      const splitName = userName.split(' ');
      const name =
        user.redmineId === Number(data.valueId) ? splitName[0] : splitName[1];
      const secondName =
        user.redmineId === Number(data.valueId) ? splitName[2] : splitName[0];
      return `${name ? name.slice(0, 1) : ''}${secondName ? secondName.slice(0, 1) : ''}`;
    },
    [user],
  );

  const handleModalAction = useCallback(
    async e => {
      switch (e.currentTarget.attributes.value.value) {
        case 'save':
          await handleDeleteIssue();
          break;
        case 'delete':
          setOpenWarningModal(false);
          setShowDeleteIssueWarning(false);
          break;
      }
    },
    [handleDeleteIssue],
  );

  const handleIssueEdit = useCallback(
    (id, data) => {
      const column =
        columns.find(
          item => item.statusId.toString() === menuIssue.services.statusId,
        ) ?? null;
      const issue =
        column.issues.find(item => item.issueId === id.toString()) ?? null;
      const pos = column.issues.map(e => e.issueId).indexOf(issue.issueId);
      if (!column && !issue) {
        return;
      }
      issue.issueName = data.find(item => item.fieldId === 'subject').valueName;
      issue.fields = issue.fields.map(field => {
        const currentField = data.find(
          item => `${item.instance}.${item.fieldId}` === field.fieldId,
        );
        if (currentField !== undefined) {
          const updatedField = field;
          const value = currentField.valueId;
          updatedField.value = !value
            ? null
            : Array.isArray(value)
              ? value.map(item => item.valueName)
              : value.valueName;
          updatedField.valueId = !value
            ? null
            : Array.isArray(value)
              ? value.map(item => item.valueName)
              : value.valueId;
          updatedField.color &&
            value &&
            value.color &&
            (updatedField.color = value.color);
          return updatedField;
        } else {
          return field;
        }
      });
      const columnPos = columns.map(e => e.statusId).indexOf(column.statusId);
      column.issues = [
        ...column.issues.slice(0, pos),
        issue,
        ...column.issues.slice(pos + 1),
      ];
      const statusId = data.find(item => item.fieldId === 'statusId').valueId;
      setColumns([
        ...columns.slice(0, columnPos),
        column,
        ...columns.slice(columnPos + 1),
      ]);
      if (menuIssue.services.statusId !== statusId.valueId) {
        moveIssue(
          menuIssue.services.statusId,
          statusId.valueId,
          issue.issueId,
          pos,
        );
      }
    },
    [columns, menuIssue, moveIssue],
  );

  const handleOpenIssue = useCallback(id => {
    setMenuIssue(id);
    setDrawerOpen(true);
  }, []);

  function Pagination({ page, onPageChange, className }) {
    return (
      <MuiPagination
        color='primary'
        className={className}
        count={pagination.count}
        siblingCount={1}
        boundaryCount={0}
        page={pagination.page + 1}
        renderItem={item => {
          if (item.type === 'next') {
            return (
              !item.disabled && (
                <Box>
                  {getLocalString('labels', 'next')}
                  <PaginationItem
                    {...item}
                    slots={{
                      next: PaginationNextButtonIcon,
                    }}
                  ></PaginationItem>
                </Box>
              )
            );
          } else if (item.type === 'previous') {
            return (
              !item.disabled && (
                <Box>
                  <PaginationItem
                    {...item}
                    slots={{
                      previous: PaginationPreviousButtonIcon,
                    }}
                  ></PaginationItem>
                  {getLocalString('labels', 'previous')}
                </Box>
              )
            );
          } else {
            return <PaginationItem {...item}></PaginationItem>;
          }
        }}
        onChange={(event, newPage) => {
          changePagination(newPage - 1);
        }}
      />
    );
  }

  return (
    <>
      {loading && (
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 5,
          }}
        >
          <CircularProgress color='secondary' />
        </Grid>
      )}
      <WarningModal
        issue
        simple
        isWarningModalOpen={showDeleteIssueWarning}
        handleToggle={handleModalAction}
        closeWarningModal={() => setShowDeleteIssueWarning(false)}
        title={getLocalString('messages', 'deleteIssue')}
      />
      <WarningModal
        issue
        simple
        isWarningModalOpen={openWarningModal}
        handleToggle={handleModalAction}
        closeWarningModal={() => setOpenWarningModal(false)}
        title={getLocalString('messages', 'confirmDelete')}
      />
      {isContextMenuOpen && (
        <ContextMenu
          open={isContextMenuOpen}
          id={menuIssue.issueId}
          position={menuPosition}
          closed={handleCloseMenu}
          select={handleSubMenuSelect}
          selectMenu={handleMenuSelect}
        ></ContextMenu>
      )}
      {drawerOpen && (
        <AgileIssue
          issue={menuIssue}
          open={drawerOpen}
          close={handleCloseIssue}
          action={handleMenuSelect}
          handleSave={handleIssueEdit}
          indicators={indicators}
        ></AgileIssue>
      )}
      <Grid
        container
        sx={{
          flexDirection: 'column',
          flexWrap: 'nowrap',
        }}
      >
        <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
          <StyledAgileTableContainer
            container
            columnSpacing={2}
            sx={{
              flexWrap: 'nowrap',
            }}
          >
            {columns.map((column, index) => (
              <Grid
                key={index}
                sx={{
                  minWidth: '295px',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Grid
                  sx={{
                    backgroundColor: '#7174AC',
                    padding: '8px 16px',
                    borderRadius: '16px 16px 0 0',
                    opacity: column.isDisabled ? '0.5' : 1,
                  }}
                >
                  <Typography
                    variant='h3'
                    sx={{
                      fontWeight: 700,
                      color: '#fff',
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box component={'span'}>{column.statusName}</Box>
                    <Box component={'span'}>{`(${column.count})`}</Box>
                  </Typography>
                </Grid>
                <Droppable
                  droppableId={column.statusId.toString()}
                  isDropDisabled={column.isDisabled}
                >
                  {provided => {
                    return (
                      <>
                        {provided.placeholder}
                        <StyledColumn
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          elevation={0}
                          sx={{
                            backgroundColor: '#E4E4EF',
                            padding: '8px',
                            borderRadius: '0 0 16px 16px',
                            width: '295px',
                          }}
                        >
                          {column.issues.length === 0 && (
                            <Typography
                              variant='h5'
                              sx={{
                                fontWeight: 700,
                                color: '#656985',
                                textAlign: 'center',
                              }}
                            >
                              {getLocalString(
                                'AgileContainer',
                                'labels',
                                'empty',
                              )}
                            </Typography>
                          )}
                          {column.issues.map((issue, index) => {
                            return (
                              <Draggable
                                key={index}
                                draggableId={issue.issueId}
                                index={index}
                              >
                                {provided => (
                                  <Box
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    sx={{
                                      backgroundColor: '#fff',
                                      borderRadius: '8px',
                                      padding: '8px',
                                      marginTop: index > 0 ? '8px' : 0,
                                    }}
                                    onContextMenu={event =>
                                      handleOpenContextMenu(event, issue)
                                    }
                                    onClick={() => handleOpenIssue(issue)}
                                  >
                                    <Box
                                      sx={{
                                        marginBottom: '4px',
                                      }}
                                    >
                                      <Typography
                                        variant='h3'
                                        sx={{
                                          fontWeight: 700,
                                          color: '#6270D0',
                                          wordBreak: 'break-word',
                                        }}
                                      >
                                        {issue.issueName}
                                      </Typography>
                                    </Box>
                                    {issue.fields
                                      .filter(item => item.fieldType !== 'user')
                                      .map((field, index) => {
                                        return (
                                          <Grid
                                            container
                                            key={field.fieldId}
                                            sx={{
                                              marginTop: index > 0 ? '2px' : 0,
                                            }}
                                          >
                                            {field.fieldType !== 'tags' ? (
                                              <>
                                                <Typography
                                                  variant='h5'
                                                  sx={{
                                                    color: '#656985',
                                                    fontWeight: 700,
                                                    marginRight: '4px',
                                                  }}
                                                >
                                                  {field.fieldName}
                                                </Typography>
                                                <Box
                                                  sx={{
                                                    fontSize: '12px',
                                                    fontWeight: 700,
                                                    color:
                                                      field.color &&
                                                      field.color !== 'default'
                                                        ? field.color
                                                        : 'inherit',
                                                  }}
                                                >
                                                  {field.value}
                                                </Box>
                                              </>
                                            ) : (
                                              <>
                                                {field.value
                                                  .filter(
                                                    (item, index) => index < 3,
                                                  )
                                                  .map((item, index) => {
                                                    return (
                                                      <StyledTag
                                                        key={index}
                                                        component={'span'}
                                                      >
                                                        <Typography
                                                          variant='h5'
                                                          sx={{
                                                            color: '#212346',
                                                            fontWeight: 700,
                                                            marginRight: '4px',
                                                          }}
                                                        >
                                                          {item}
                                                        </Typography>
                                                      </StyledTag>
                                                    );
                                                  })}
                                                {field.value.length > 3 && (
                                                  <StyledTag component={'span'}>
                                                    <Typography
                                                      variant='h5'
                                                      sx={{
                                                        color: '#212346',
                                                        fontWeight: 700,
                                                        marginRight: '4px',
                                                      }}
                                                    >
                                                      {`+${field.value.length - 3}`}
                                                    </Typography>
                                                  </StyledTag>
                                                )}
                                              </>
                                            )}
                                          </Grid>
                                        );
                                      })}
                                    {issue.fields
                                      .filter(item => item.fieldType === 'user')
                                      .map((field, index) => {
                                        return (
                                          <Grid
                                            container
                                            key={index}
                                            sx={{
                                              alignItems: 'center',
                                              marginTop:
                                                field.value &&
                                                field.value !== ''
                                                  ? '8px'
                                                  : 0,
                                            }}
                                          >
                                            {field.value &&
                                              field.value !== '' && (
                                                <StyledAvatar
                                                  sx={{
                                                    fontWeight: '700',
                                                    marginRight: '8px',
                                                    textTransform: 'capitalize',
                                                    borderRadius: '100%',
                                                    color: '#fff',
                                                  }}
                                                >
                                                  {getName(field)}
                                                </StyledAvatar>
                                              )}
                                            <Typography
                                              variant='h5'
                                              sx={{
                                                color: '#212346',
                                                fontWeight: 700,
                                              }}
                                            >
                                              {field.value}
                                            </Typography>
                                          </Grid>
                                        );
                                      })}
                                  </Box>
                                )}
                              </Draggable>
                            );
                          })}
                        </StyledColumn>
                      </>
                    );
                  }}
                </Droppable>
              </Grid>
            ))}
          </StyledAgileTableContainer>
        </DragDropContext>
        <StyledPaginationContainer
          sx={{
            marginTop: '20px',
          }}
        >
          <Pagination></Pagination>
        </StyledPaginationContainer>
      </Grid>
    </>
  );
};

export default AgileTable;
